import React, { useState } from "react";
import Tooltip from "@material-ui/core/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import { format } from "date-fns";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import AppBar from "@mui/material/AppBar";
import Container from "../../GeneralComponents/Container";
import { Topbar, Sidebar, Table } from "../components";
import {
  deleteVideoCategory,
  getVideoCategories,
} from "../../../redux/actions/apiActions";
import setGlobalLoader from "../../../redux/actions/loaderActions";
import exportToExcel from "../../../utils/exportToExcel";

const TableComponent = ({ columns, data, title, isRemoteData }) => {
  const theme = useTheme();

  return (
    <Box p={4}>
      <Box
        width={1}
        height={1}
        // minHeight={800}
        borderRadius={2}
        border={`2px solid ${theme.palette.divider}`}
        sx={{
          borderStyle: "dashed",
        }}
      >
        <Table
          columns={columns}
          data={data}
          export={false}
          // search
          selection={false}
          toolbar
          title={title}
          isRemoteData={isRemoteData}
        />
      </Box>
    </Box>
  );
};

const VideoCategories = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [videoCategory, setVideoCategory] = useState([]);
  const theme = useTheme();
  const role = useSelector((state) => state.authReducer.role);
  const isMd = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });

  const [openSidebar, setOpenSidebar] = useState(false);

  const handleSidebarOpen = () => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = () => {
    setOpenSidebar(false);
  };

  const open = isMd ? false : openSidebar;

  const handleOnDelete = async (rowData) => {
    let text = `Are you sure you want to delete ${rowData.title}`;
    if (window.confirm(text) === true) {
      try {
        dispatch(setGlobalLoader(true));
        await deleteVideoCategory(rowData._id);
        setVideoCategory(videoCategory.filter((d) => d._id !== rowData._id));
        toast.success("Video Category deleted successfully");
        dispatch(setGlobalLoader(false));
      } catch (error) {
        dispatch(setGlobalLoader(false));
        console.log("Delete Video Category error", error);
        toast.error(
          error.response.data.msg ??
          "some error occured while deleting Vedio category"
        );
      }
    } else {
      toast.success("Request Cancelled");
    }
  };

  const headers = ["Sr no.", "Title", "Image", "Created At"];
  const columns = [
    { title: "Sr no.", field: "index", filtering: false },
    { title: "Title", field: "title", filtering: false },
    {
      title: "Image",
      filtering: false,
      render: (rowData) =>
        rowData.imageUrl && rowData.imageUrl ? (
          <img
            src={rowData.imageUrl}
            alt="Video Category"
            style={{ width: 160, height: 100, objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          "-"
        ),
    },
    { title: "Created At", field: "createdAt", filtering: false },
    {
      title: "Actions",
      field: "",
      filtering: false,
      render: (rowData) => (
        <div className="tableActions ">
          <Tooltip title="Delete">
            <DeleteIcon onClick={() => handleOnDelete(rowData)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Fetch data with server-side pagination
  const fetchVideoCategories = async (query) => {
    return new Promise(async (resolve) => {
      try {
        dispatch(setGlobalLoader(true));

        // Build query string for API
        const page = query.page + 1; // MaterialTable uses 0-based index, API uses 1-based
        const limit = query.pageSize;
        const sort = query.orderBy ? { [query.orderBy.field]: query.orderDirection } : {};

        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        // Add sort if exists
        if (Object.keys(sort).length > 0) {
          queryParams.append('sort', JSON.stringify(sort));
        }

        const vidRes = await getVideoCategories(`?${queryParams.toString()}`);
        const apiData = vidRes?.data?.data;

        const data = apiData?.docs?.map((d, i) => ({
          ...d,
          imageUrl: d?.imageUrl ?? "",
          index: (page - 1) * limit + i + 1,
          createdAt: d?.createdAt ? format(new Date(d.createdAt), "MMM dd, yyyy") : "-",
        }));

        dispatch(setGlobalLoader(false));

        resolve({
          data: data,
          page: query.page,
          totalCount: apiData?.total || 0,
        });
      } catch (error) {
        dispatch(setGlobalLoader(false));
        console.log("get Video Category error", error);
        toast.error(error?.response?.data?.msg || "Error fetching video categories");
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
      }
    });
  };

  return (
    <Box>
      <AppBar
        position={"fixed"}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? "permanent" : "temporary"}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <Box
          display="flex"
          flex="1 1 auto"
          overflow="hidden"
          paddingLeft={{ md: "256px" }}
        >
          <Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto">
              <Box
                marginTop={3}
                mx={{ xs: 3, md: 4 }}
                display={"flex"}
                justifyContent={"space-between"}
              >
                <Button
                  variant="contained"
                  color="primary"
                  component="button"
                  size="medium"
                  onClick={() => {
                    navigate("/video-category/add");
                  }}
                >
                  Add Video Category
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  component="button"
                  size="medium"
                  onClick={async () => {
                    try {
                      dispatch(setGlobalLoader(true));
                      // Fetch all video categories for export
                      const vidRes = await getVideoCategories("?limit=10000");
                      const allVideoCategories = vidRes?.data?.data?.docs?.map((d, i) => ({
                        ...d,
                        imageUrl: d?.imageUrl ?? "",
                        index: i + 1,
                        createdAt: d?.createdAt ? format(new Date(d.createdAt), "MMM dd, yyyy") : "-",
                      }));

                      const rows = allVideoCategories.map((row) => ({
                        "Sr No. ": row.index,
                        name: row.title,
                        image: row.imageUrl,
                        createdAt: row.createdAt,
                      }));
                      await exportToExcel(rows, headers, "Video-Categories");
                      dispatch(setGlobalLoader(false));
                    } catch (error) {
                      dispatch(setGlobalLoader(false));
                      console.log("export error", error);
                      toast.error("Error exporting video categories");
                    }
                  }}
                >
                  Download
                </Button>
              </Box>
              <TableComponent
                data={fetchVideoCategories}
                columns={columns}
                title="Video Categories"
                isRemoteData={true}
              />
              <Divider />
              {/* <Container paddingY={4}>
                <Footer />
              </Container> */}
            </Box>
          </Box>
        </Box>
      </main>
    </Box>
  );
};

export default VideoCategories;
